/*
 * Autorska prava pridržava Mali mol d.o.o. Pula
 * Copyright (c) 2016. by Mali mol d.o.o. Pula. All rights reserved.
 *
 * Korištenje ili javno objavljivanje ovog programskog koda ili bilo kojeg
 * njegovog dijela, nije dozvoljeno bez prethodnog pismenog odobrenja tvrtke Mali mol d.o.o.
 *
 * Mali mol d.o.o., Špiljavac 2, 52100 Pula, Croatia
 * <URL:http://www.malimol.hr>, <mailto:info@malimol.hr>
 */

import { ElementName } from '../../enums/ElementName';
import { ElementType } from '../../enums/ElementType';
import { FilterMatchMode } from 'primevue/api';
import type { ICrossSection } from '@/types/ICrossSection';
import type { IFilterDataString } from '@/types/IFilterDataString';
import moment from 'moment';

export const useUtility = () => {
  const SectionType = {
    CIRCLE: 0,
    OVAL: 1,
    ASYMMETRIC: 2
  };

  const sectionImages = [
    {
      type: 0,
      orientation: 0,
      image: 'images/cross_circle.svg'
    },
    {
      type: 1,
      orientation: 0,
      image: 'images/cross_oval_orient_1.svg'
    },
    {
      type: 1,
      orientation: 90,
      image: 'images/cross_oval_orient_2.svg'
    },
    {
      type: 2,
      orientation: 0,
      image: 'images/cross_assymetric_oval_orient_1.svg'
    },
    {
      type: 2,
      orientation: 90,
      image: 'images/cross_assymetric_oval_orient_2.svg'
    },
    {
      type: 2,
      orientation: 180,
      image: 'images/cross_assymetric_oval_orient_3.svg'
    },
    {
      type: 2,
      orientation: 270,
      image: 'images/cross_assymetric_oval_orient_4.svg'
    }
  ];

  const jointTypes = [
    { type: 0, image: 'images/joint_1.svg' },
    { type: 1, image: 'images/joint_2.svg' },
    { type: 2, image: 'images/joint_3.svg' },
    { type: 3, image: 'images/joint_4.svg' }
  ];

  const getSectionParameters = (
    section: ICrossSection | null
  ): string | null => {
    if (section) {
      switch (section.type) {
        case SectionType.CIRCLE:
          return 'd=' + section.d1 + ' D=' + section.dv1;
        case SectionType.OVAL:
          return 'd=' + section.d1 + ' D=' + section.dv1 + ' L=' + section.l;
        case SectionType.ASYMMETRIC:
          return (
            'd=' +
            section.d1 +
            ' D=' +
            section.dv1 +
            ' L=' +
            section.l +
            'd2=' +
            section.d2 +
            ' D2=' +
            section.dv2
          );
        default:
          return null;
      }
    } else {
      return null;
    }
  };

  const getSectionImage = (section: ICrossSection | null | undefined): string | undefined => {
    if (section) {
      let found = sectionImages.find((element) => {
        return (
          element.type == section.type &&
          element.orientation == section.orientation
        );
      });
      if (found) return found.image;
      return undefined;
    } else {
      return undefined;
    }
  };

  const getJointTypeImage = (type: number): string => {
    if (type >= 0 && type < 4) return jointTypes[type].image;
    return '';
  };

  const isValidMailAddress = (mail: string): boolean => {
    return (
      mail?.length == 0 ||
      /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(mail!)
    );
  }

  const getFirstWord = (text: string): string => {
    return text? text.split(' ')[0]: '';
  }

  const getLastWord = (text: string): string => {
    return text? text.split(' ').pop()!: '';
  }

  const objectIsEmpty = (obj: any): boolean => {
    if(obj === null || obj === undefined) return true;
    if(typeof obj === 'string') return obj.length === 0;
    return Object.keys(obj).length === 0;
  }

  /**
   * Vraća element unutar objekta koji nije deklariran u tipu objekta, ukoliko postoji.
   * Kako je Typescript, za razliku od Javascripta, rigidan što se tiče deklaracija objekata,
   * a ponekad je potrebno malo zaobići ta kruta pravila, ovo je način za
   * dohvatiti nedeklarirani sadržaj, ako postoji.
   * @param data objekt iz kojeg treba izdvojiti zadano polje
   * @param field naziv zadanog polja
   * @returns sadržaj polja, ako postoji, inače null
   */
  const resolveFieldData = (data: any, field: string) => {
    if (data && Object.keys(data).length && field) {
      if (field.indexOf('.') === -1) {
        return data[field];
      } else {
        let fields = field.split('.');
        let value = data;
        for (var i = 0, len = fields.length; i < len; ++i) {
          if (value == null) {
            return null;
          }
          value = value[fields[i]];
        }
        return value;
      }
    } else {
      return null;
    }
  };

  /**
   * Postavlja vrijednost nekog polja u objektu na zadanu vrijednost, ukoliko već
   * postoji unutar objekta, ili ubacuje novo polje u objekt koji takvo polje nema deklarirano.
   * To radimo kada zadani objekt nema u deklaraciji navedeno takvo polje ili je tipa any
   * pa mu Typestript ne može provjeriti deklaraciju.
   * @param data objekt kojemu mijenjamo/dodajemo polje,
   * @param field naziv polja kojeg mijenjamo/dodajemo
   * @param value sadržaj polja
   * @returns void
   */
  const setFieldData = (data: any, field: string, value: any) => {
    if (data && field) {
      if (field.indexOf('.') === -1) {
        data[field] = value;
      } else {
        let fields = field.split('.');
        let item = data;
        for (var i = 0, len = fields.length; i < len; ++i) {
          if (item == null) {
            return;
          }
          item = item[fields[i]];
        }
        item = value;
      }
    }
  };

  /**
   * Vraća element unutar objekta koji nije deklariran u tipu objekta. Kako je Typescript, za razliku od Javascripta,
   * rigidan što se tiče deklaracija objekata, a ponekad je potrebno malo zaobići ta kruta pravila, ovo je način za
   * dohvatiti nedeklarirani element, ako postoji.
   * @param option objekt iz kojeg treba dohvatiti zadano polje
   * @param accessKey nauiv zadanog polja
   * @returns sadržaj zadanog polja, ako postoji, inače undefined.
   */
  const getField = (option: any, accessKey: string | undefined) => {
    return accessKey ? resolveFieldData(option, accessKey) : undefined;
  };

  const setField = (option: any, value: any, accessKey: string | undefined) => {
    if (accessKey) {
      setFieldData(option, accessKey, value);
    } else {
      option = value;
    }
  };

  /**
   * Enkodira zadani string u base64 format
   * @param s string koji treba enkodirati
   * @returns base64 enkodirani sadržaj.
   */
  const base64 = (s: string) => {
    return window.btoa(unescape(encodeURIComponent(s)));
  }

  /**
   * Vraća string prema zadanom formatu. Koristi placeholder u sFormat stringu u obliku {placeholder} i mijenja
   * ga zadanim sInsert stringom te vraća rezultat.
   * @param sFormat format string u kojem se nalazi placeholder kojeg treba zamijeniti
   * @param sInsert tekst koji treba ubaciti umjesto placeholdera
   * @returns kombinirani string
   */
  const format = (sFormat: string, sInsert: string) => {
    return sFormat.replace(/{(\w+)}/g, sInsert);
  }

  /**
   * Iz dokumenta izdvaja tabelu i formatira ju u oblik pogodan za import u Excel te kreirea download link
   * za preuzimanje tako formatirane datoteke.
   * @param tableId ID tabele unutar dokumenta koju treba izvesti u excel formatu
   * @param filename naziv daoteke koja će biti predložena za download
   */
  const exportExcel = (tableId: string, filename: string) => {
    let dataType = 'data:application/vnd.ms-excel;base64, ';
    let template = '<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40"><meta http-equiv="content-type" content="application/vnd.ms-excel; charset=UTF-8"><head><!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>Sheet 1</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]--></head><body>{table}</body></html>';

    console.log("exportExcel: tableId = " + tableId);
    console.log("exportExcel: filename = " + filename);
    let tableSelect = document.getElementById(tableId);
    console.log("exportExcel: tableSelect:");
    console.log(tableSelect);
    let tableHTML = tableSelect!.outerHTML;
    tableHTML = tableHTML.replace(/&nbsp;/g, "");
    console.log("tableHTML", tableHTML);
    var doc = new DOMParser().parseFromString(tableHTML, 'text/html');
    console.log("doc:");
    console.log(doc);
    let header = doc.getElementById('header');
    if (header) {
      header!.parentNode!.removeChild(header!);
    }
    tableSelect = doc.getElementById(tableId);
    console.log("tableSelect:");
    console.log(tableSelect);
    tableHTML = tableSelect!.innerHTML
      .replace(/<!---->/g, "")
      .replace(/ kg <\/span>/g, "</span>")
      .replace(/ kg<\/span>/g, "</span>")
      .replace(/ € <\/span>/g, "</span>")
      .replace(/ €<\/span>/g, "</span>")
      .replace(/ min <\/span>/g, "</span>")
      .replace(/ min<\/span>/g, "</span>")
      .replace(/ m<sup data-v-(.*?)="">2<\/sup>/g, "");
    console.log('tableHTML: ', tableHTML);

    // Specify file name
    filename = filename ? filename + '.xls' : 'excel_data.xls';

    // Create download link element
    let downloadLink = document.createElement('a');

    document.body.appendChild(downloadLink);

    // Create a link to the file
    downloadLink.href = dataType + base64(format(template, tableHTML));

    // Setting the file name
    downloadLink.download = filename;

    //triggering the function
    downloadLink.click();
  };

  /**
   * Prebacuje elemente iz source polja u target polje, ako zadovoljavaju uvjet moveCheck funkcije.
   * U moveCheck funkciji se najčešće provjerava da element iz source polja već ne postoji u target polju.
   * Za čisto prebacivanje podataka koristiti moveElements(source, target, () => true)
   * @param source polje iz kojeg se prebacuju elementi
   * @param target polje u koje se prebacuju elementi
   * @param moveCheck callback funkcija koja provjerava da li se element iz source polja treba prebaciti u target polje
   */
  function moveElements(
    source: Array<any>,
    target: Array<any>,
    moveCheck: Function
  ) {
    for (var i = 0; i < source.length; i++) {
      var element = source[i];
      if (moveCheck(element)) {
        source.splice(i, 1);
        target.push(element);
        i--;
      }
    }
  }

  /**
   * Provjera jesu odgovara li target polje referentnom polju. Usporedba
   * jednakosti vrši se preko polja _id u objektima polja.
   * @param target polje koje uspoređujemo
   * @param reference polje s kojim uspoređujemo
   * @returns true ako su polja identična, inače false
   */
  const equalArrays = (target: Array<any>, reference: Array<any>): boolean => {
    if (target === reference) return true;
    if (target == null || reference == null) return false;
    if (target.length !== reference.length) return false;
    let equals = true;
    reference.forEach((element: any) => {
      let found = target.find((item: any) => item._id === element._id);
      if (!found) {
        equals = false;
        return;
      }
    });
    return equals;
  };

  const getSketchStatusName = (status: number): string => {
    switch (status) {
      case 1:
        return 'sketch_grid.ST_INREVISION';
      case 2:
        return 'sketch_grid.ST_IMPORTED';
      case 3:
        return 'sketch_grid.ST_APPROVED';
      case 4:
        return 'sketch_grid.ST_PREPARED';
      case 5:
        return 'sketch_grid.ST_INPRODUCTION';
      case 6:
        return 'sketch_grid.ST_PRODUCED';
      case 7:
        return 'sketch_grid.ST_INDELIVERY';
      case 8:
        return 'sketch_grid.ST_DELIVERED';
      case 9:
        return 'sketch_grid.ST_BILLED';
      case 10:
        return 'sketch_grid.ST_INSTALLED';
      default:
        return 'sketch_grid.unknown';
    }
  }

  const getSketchTypeName = (type: number): string => {
    switch (type) {
      case 0:
        return 'sketch_grid.pipeline_type';
      case 1:
        return 'sketch_grid.ventilation_type';
      case 2:
        return 'sketch_grid.cap_type';
      case 3:
        return 'sketch_grid.catalog_type';
      case 4:
        return 'sketch_grid.container_type';
      default:
        return 'sketch_grid.unknown';
    }
  };

  const getElementTypeName = (type: number): string => {
    switch (type) {
      case ElementType.PIPE:
        return ElementName.PIPE;
      case ElementType.ELBOW:
        return ElementName.ELBOW;
      case ElementType.REDUCTION:
        return ElementName.REDUCTION;
      case ElementType.TAP:
        return ElementName.TAP;
      case ElementType.FLATTENING:
        return ElementName.FLATTENING;
      case ElementType.FLANGE:
        return ElementName.FLANGE;
      case ElementType.PUSH_COVER:
        return ElementName.PUSH_COVER;
      case ElementType.RIBBED_COVER:
        return ElementName.RIBBED_COVER;
      case ElementType.DOUBLE_ELBOW:
        return ElementName.DOUBLE_ELBOW;
      case ElementType.FLANGE_CAP:
        return ElementName.FLANGE_CAP;
      case ElementType.VALVE_CAP:
        return ElementName.VALVE_CAP;
      case ElementType.COFFER_VALVE_CAP:
        return ElementName.COFFER_VALVE_CAP;
      case ElementType.COFFER_CAP:
        return ElementName.COFFER_CAP;
      case ElementType.OVAL_CAP_REGULAR:
        return ElementName.OVAL_CAP_REGULAR;
      case ElementType.OVAL_CAP_UNREGULAR:
        return ElementName.OVAL_CAP_UNREGULAR;
      default:
        return 'element.unknown';
    }
  };

  const formatDate = (date: Date, format: string): string => {
    return moment(date).format(format);
  };

  const createDebounce = <T>(fn: Function, wait: number) => {
    let timer: ReturnType<typeof setTimeout>;
    return (event: T) => {
      if (timer)
        clearTimeout(timer)
      timer = setTimeout(() => {
        if (typeof fn === 'function') {
          fn(event)
        }
      }, wait)
    }
  }

    const setFilterMode = (filter: IFilterDataString, value: string) => {
      let firstChar = value.charAt(0);
      if(firstChar === '^') {
        filter.matchMode = FilterMatchMode.STARTS_WITH;
        filter.value = value.slice(1).trim();
      } else {
        filter.matchMode = FilterMatchMode.CONTAINS;
        filter.value = value;
      }
    }

  return {
    getSectionParameters,
    getSectionImage,
    getJointTypeImage,
    getSketchStatusName,
    jointTypes,
    resolveFieldData,
    setFieldData,
    getField,
    setField,
    moveElements,
    equalArrays,
    getSketchTypeName,
    getElementTypeName,
    formatDate,
    exportExcel,
    createDebounce,
    setFilterMode,
    isValidMailAddress,
    getFirstWord,
    getLastWord,
    objectIsEmpty
  };
};
